import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { subHours } from "date-fns";
import { useCallback, useEffect, useState } from "react";

import ReactGA from "react-ga4";
import { useLocation, useParams } from "react-router-dom";
import { blogApi } from "src/api/blog";
import { BlogShareButtons } from "src/components/blog-share-buttons";
import { Seo } from "src/components/seo";
import { useMounted } from "src/hooks/use-mounted";
import { usePageView } from "src/hooks/use-page-view";
import { PostContent } from "src/sections/dashboard/blog/post-content";
import { PostNewsletter } from "src/sections/dashboard/blog/post-newsletter";
import type { Post } from "src/types/blog";

interface Comment {
  id: string;
  authorAvatar: string;
  authorName: string;
  authorRole: string;
  content: string;
  createdAt: number;
  isLiked: boolean;
  likes: number;
}

const useComments = (): Comment[] => {
  return [
    {
      id: "d0ab3d02ef737fa6b007e35d",
      authorAvatar: "/assets/avatars/avatar-alcides-antonio.png",
      authorName: "Alcides Antonio",
      authorRole: "Product Designer",
      content:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
      createdAt: subHours(new Date(), 2).getTime(),
      isLiked: true,
      likes: 12,
    },
    {
      id: "3ac1e17289e38a84108efdf3",
      authorAvatar: "/assets/avatars/avatar-jie-yan-song.png",
      authorName: "Jie Yan Song",
      authorRole: "Web Developer",
      content:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor.",
      createdAt: subHours(new Date(), 8).getTime(),
      isLiked: false,
      likes: 8,
    },
  ];
};

const usePost = (title: string | undefined): Post | null => {
  const isMounted = useMounted();
  const [post, setPost] = useState<Post | null>(null);
  const handlePostGet = useCallback(async () => {
    if (!title) {
      return;
    }
    try {
      const response = await blogApi.getPost(title);
      if (isMounted()) {
        setPost(response);
      }
    } catch (err) {
      console.error(err);
      if (isMounted()) {
        setPost(null);
      }
    }
  }, [isMounted, title]);

  useEffect(() => {
    handlePostGet();
  }, [handlePostGet]);

  return post;
};

const Page = () => {
  usePageView();
  const { title: titleParam } = useParams<{ title: string }>();
  const location = useLocation();
  // Fallback to pathname extraction if useParams doesn't work
  const title = titleParam || location.pathname.split("/").pop();

  const post = usePost(title);

  if (!post) {
    return null;
  }

  const publishedAtISO = new Date(post.publishedAt).toISOString();

  const getCurrentPageUrl = () => {
    if (typeof window !== "undefined") {
      return window.location.href;
    }
    return "";
  };

  // Article structured data
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.shortDescription || post.title,
    image: process.env.REACT_APP_BASE_URL_LANDING,
    datePublished: publishedAtISO,
    dateModified: publishedAtISO,
    author: {
      "@type": "Organization",
      name: "Opensource.clinic",
      url: process.env.REACT_APP_BASE_URL_LANDING,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": post.path
        ? `https://www.opensource.clinic${post.path}`
        : `${process.env.REACT_APP_BASE_URL_LANDING}/blog/${title}`,
    },
  };

  const canonicalUrl = post.path
    ? `https://www.opensource.clinic${post.path}`
    : undefined;

  return (
    <>
      <Seo
        title={post.seoTitle || post.title}
        description={post.shortDescription || post.title}
        type="article"
        image={post.cover || undefined}
        canonical={canonicalUrl}
        structuredData={articleSchema}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 15,
        }}
      >
        <Container maxWidth="md">
          <article>
            <Stack spacing={3}>
              <div>
                <Chip label={post.category} />
              </div>
              <Typography component="h1" variant="h3">
                {post.title}
              </Typography>
              <Typography color="text.secondary" variant="subtitle1">
                {post.shortDescription}
              </Typography>
              <Stack
                alignItems="center"
                direction="row"
                spacing={2}
                sx={{ mt: 3 }}
              >
                {/* <Avatar src={post.author.avatar} /> */}
                <div>
                  <Typography variant="subtitle2">By OSC</Typography>
                  <Typography color="text.secondary" variant="body2">
                    {post.readTime} read
                  </Typography>
                </div>
              </Stack>
              <Box sx={{ mt: 2 }}>
                <BlogShareButtons
                  url={getCurrentPageUrl()}
                  title={post.title}
                  description={post.shortDescription}
                  showLabels={true}
                />
              </Box>
            </Stack>
            {post.cover && !post.cover.includes("KK") && (
              <Box
                component="img"
                src={post.cover}
                alt={`${post.title} - Featured image`}
                sx={{
                  width: "100%",
                  maxHeight: 500,
                  objectFit: "contain",
                  borderRadius: 1,
                  mt: 3,
                }}
              />
            )}
            {post.content && (
              <Container sx={{ py: 3 }}>
                <PostContent content={post.content} />
              </Container>
            )}
            <Divider sx={{ my: 3 }} />

            <Box sx={{ mt: 4 }}>
              <PostNewsletter />
            </Box>
          </article>
        </Container>
      </Box>
    </>
  );
};

export default Page;
